import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import i18n from "i18n";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetUsersQuery,
  useUpdateUserMutation,
} from "reduxFiles/api/userApi";
import { enumToOptions } from "services/helper";
import { User, UserRole, UserStatus } from "types/userTypes";
import { Column, TableAction } from "types/globalTypes";

const Users = () => {
  // hooks
  const { t } = useTranslation();
  const lang = i18n.language;

  // states
  const [item, setItem] = useState<User | null>(null);
  const confirmModal = useModal();
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetUsersQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters: filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateUserMutation();
  const [updateApi, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeleteUserMutation();

  // form and table
  const fields: Field[] = [
    {
      name: "fullName",
      label: t("user.fullname"),
      type: "text",
      formed: true,
      required: true,
    },
    {
      name: "fullname",
      label: t("user.fullname"),
      type: "text",
      filtered: true,
    },
    {
      name: "email",
      label: t("user.email"),
      type: "email",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "password",
      label: t("user.password"),
      type: "password",
      formed: item ? false : true,
      required: true,
    },
    {
      name: "mobile",
      label: t("user.mobile"),
      type: "mobile",
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "role",
      label: t("user.role"),
      type: "select",
      options: enumToOptions(UserRole, t, "user.roles"),
      filtered: true,
    },
    {
      name: "status",
      label: t("user.status"),
      type: "select",
      options: enumToOptions(UserStatus, t, "user.statuses"),
      filtered: true,
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<User>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "fullname",
      title: t("user.fullname"),
    },
    { key: "email", title: t("user.email") },
    { key: "mobile", title: t("user.mobile") },
    {
      key: "role",
      title: t("user.role"),
      render: (row) => t(`user.roles.${row.role}`),
    },
    {
      key: "status",
      title: t("user.status"),
      render: (row) => t(`user.statuses.${row.status}`),
    },
  ];

  const actions: TableAction<User>[] = [
    {
      label: t("user.editUser"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    {
      label: t("user.deleteUser"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (formData: FieldValues) => {
    try {
      if (item) {
        await updateApi({ id: item.id, data: formData }).unwrap();
      } else {
        await addApi(formData).unwrap();
      }
      modal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("user.userList")}
          createBtn={{
            title: t("user.addUser"),
            handler: modal.openModal,
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<User>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={
            item
              ? {
                  ...item,
                  fullName: item.fullname,
                }
              : {}
          }
          title={item ? t("user.editUser") : t("user.addUser")}
          isEdit={!!item}
          loading={item ? isUpdating : isAdding}
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("user.deleteUser")}
          desc={t("actions.confirmDeleteDesc", { name: item?.fullname || "" })}
        />
      </div>
    </div>
  );
};

export default Users;
